import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Button, TextField, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import HistoryIcon from "@mui/icons-material/History";
import { axiosConfig } from "../../axios/axiosConfig";
import PaginationControls from "../Pagination/PaginationControls";

const ACCENT = "#0F766E";
const PAGE_SIZE = 50;

const ActiveUsersYesterdayPage = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFrom, setAppliedFrom] = useState("");
  const [appliedTo, setAppliedTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (appliedFrom) params.append("from_date", appliedFrom);
        if (appliedTo) params.append("to_date", appliedTo);
        params.append("page", page);
        const res = await axiosConfig({ method: "get", url: `/investors/yesterday-active-user/?${params}` });
        setTotalCount(res?.data?.count || 0);
        setUsers(res?.data?.results || []);
      } catch (e) {
        setError("Failed to fetch data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [appliedFrom, appliedTo, page]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFrom(fromDate);
    setAppliedTo(toDate);
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (appliedFrom) params.append("from_date", appliedFrom);
      if (appliedTo) params.append("to_date", appliedTo);
      params.append("download", "true");
      const res = await axiosConfig({
        method: "get",
        url: `/investors/yesterday-active-user/?${params}`,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `active_users_yesterday_${appliedFrom}_${appliedTo}.csv`.toLowerCase());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("download error:", e);
    } finally {
      setDownloading(false);
    }
  };

  const serialOffset = (page - 1) * PAGE_SIZE;

  return (
    <Box sx={{ background: "#f1f5f9", minHeight: "100vh" }}>
      <Box sx={{ position: "sticky", top: 0, zIndex: 100, background: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box onClick={() => navigate("/investor")} sx={{ width: 34, height: 34, borderRadius: "8px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "#f1f5f9" } }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </Box>
          <Box sx={{ width: 38, height: 38, borderRadius: "10px", background: ACCENT, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <HistoryIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>Active Users</Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>Users active yesterday</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <TextField type="date" size="small" label="From" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }} />
          <TextField type="date" size="small" label="To" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }}
            sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 13, background: "#fff" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" } }} />
          <Button variant="contained" size="small" startIcon={<FilterAltIcon sx={{ fontSize: 16 }} />} onClick={handleSearch}
            sx={{ height: 36, borderRadius: "8px", background: "#04343a", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, "&:hover": { background: "#016551" } }}>
            Filter
          </Button>
          <Button variant="contained" size="small"
            startIcon={downloading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleDownload} disabled={downloading}
            sx={{ height: 36, borderRadius: "8px", background: "#016551", textTransform: "none", fontSize: 13, fontWeight: 600, px: 2, boxShadow: "0 4px 12px rgba(1,101,81,0.3)", "&:hover": { background: "linear-gradient(135deg,#1db954,#17a349)" } }}>
            {downloading ? "Downloading…" : "Download CSV"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
        {error && <Alert severity="error" sx={{ borderRadius: "10px" }}>{error}</Alert>}

        <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <Box sx={{ px: 2.5, py: 1.8, background: ACCENT, borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>ACTIVE USERS (YESTERDAY)</Typography>
            {!loading && <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{totalCount.toLocaleString("en-IN")} users</Typography>}
          </Box>

          <TableContainer sx={{ overflowX: "auto", maxHeight: "calc(100vh - 200px)" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["#", "Name", "Phone", "Email", "Status"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 6 }}><CircularProgress size={28} sx={{ color: ACCENT }} /></TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 5, fontSize: 13, color: "#94a3b8" }}>No active users found</TableCell></TableRow>
                ) : (
                  users.map((row, i) => (
                    <TableRow key={row.id ?? i} sx={{ "&:hover": { background: "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{serialOffset + i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>{row.username || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#475569" }}>{row.phoneno || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: "#64748b" }}>{row.email || "—"}</TableCell>
                      <TableCell>
                        <Chip label="Active" size="small" sx={{ fontSize: 10, fontWeight: 700, background: "#ccfbf1", color: ACCENT, height: 20 }} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && totalCount > 0 && (
            <PaginationControls totalCount={totalCount} page={page} setPage={setPage} offset={PAGE_SIZE} />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ActiveUsersYesterdayPage;
